import { useMemo, useState } from 'react';
import { DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import type { NumKey, RankedRow } from './rankedRows';

const NUM_TIPS: Record<NumKey, [string, string]> = {
  users: ['Users', 'Distinct accounts — a guest one is made on a reader\'s first page view, so this is roughly distinct readers.'],
  visits: ['Sessions', 'Distinct browser-tab sessions. A session ends with the tab, so one reader coming back is a second session.'],
  events: ['Events', 'How many times it happened.'],
};

/**
 * A ranked list with its own search-and-filter bar.
 *
 * These filters narrow the ROWS — which pages, which simulations. Who the
 * numbers are about is the page's filter bar above, which every table shares;
 * the two are kept apart so a table can never quietly describe different
 * sessions from the cards beside it.
 */
export function Table({ title, rows, labelHead, filters = [], numbers = ['users', 'visits', 'events'], sortBy = 'events' }: {
  title: string;
  labelHead: string;
  rows: RankedRow[];
  filters?: FilterDef[];
  numbers?: NumKey[];
  sortBy?: NumKey;
}) {
  const [sort, setSort] = useState<SortState>({ sortBy, sortOrder: 'desc' });
  const [values, setValues] = useState<Record<string, string>>({});
  const shown = useMemo(() => {
    const q = (values.search ?? '').trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.search.includes(q))
      && filters.every(f => !values[f.key] || (r.facets[f.key] ?? []).includes(values[f.key])));
  }, [rows, values, filters]);
  const columns: ColumnDef<RankedRow>[] = [
    { key: 'label', header: labelHead, sortable: true, sortValue: r => r.label.toLowerCase(),
      render: r => (
        <>
          <span className="sa-label">{r.label}</span>
          {r.sub && <span className="sa-row-sub">{r.sub}</span>}
        </>
      ) },
    ...numbers.map((key): ColumnDef<RankedRow> => ({
      key, header: <span title={NUM_TIPS[key][1]}>{NUM_TIPS[key][0]}</span>, sortable: true, className: 'sa-num',
      sortValue: r => r[key] ?? 0,
      render: r => <span className={key === 'users' && r.users === 0 ? 'sa-zero' : undefined}>{(r[key] ?? 0).toLocaleString()}</span>,
    })),
  ];
  const active = Object.values(values).some(Boolean);
  return (
    <section className="sa-panel">
      <h2>
        {title}
        <span className="sa-count">{active ? `${shown.length} of ${rows.length}` : rows.length}</span>
      </h2>
      {rows.length === 0
        ? <p className="sa-empty">Nothing recorded in this window.</p>
        : (
          <>
            <SearchFilterBar
              filters={filters}
              values={values}
              onChange={setValues}
              searchPlaceholder={`Search ${labelHead.toLowerCase()}…`}
              hideFetch
              subtle
            />
            <DataTable<RankedRow>
              columns={columns}
              data={shown}
              rowKey={r => r.key}
              sort={sort}
              onSortChange={setSort}
              emptyMessage="Nothing matches these filters."
              compact
            />
          </>
        )}
    </section>
  );
}
